import { trackGA4ViewCart, trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { SentryError } from '@ifixit/sentry';
import { convertCartLineItemsToAnalyticsItem } from '@ifixit/shopify-cart-sdk';
import { useCart } from '@ifixit/shopify-cart-sdk/hooks';
import { useDisclosure } from '@ifixit/ui/hooks/use-disclosure';
import { createContext, useMemo, useContext, useCallback } from 'react';
import type { PropsWithChildren } from 'react';

export type CartDrawerContext = ReturnType<typeof useDisclosure>;
const CartDrawerContext = createContext<CartDrawerContext | null>(null);

export function CartDrawerProvider({ children }: PropsWithChildren) {
   const disclosure = useDisclosure();

   const value = useMemo((): CartDrawerContext => disclosure, [disclosure.isOpen]);

   return <CartDrawerContext.Provider value={value}>{children}</CartDrawerContext.Provider>;
}

export function useCartDrawer() {
   const context = useContext(CartDrawerContext);
   if (context === null) {
      throw new SentryError('useCartDrawer must be used within a CartDrawerProvider');
   }
   const { onOpen, onClose } = context;
   const onViewCart = useCallback(() => {
      trackAnalyticsCustomEvent({
         eventCategory: 'Mini Cart',
         eventAction: 'Btn "View Cart" - Click',
         eventName: `${window.location.origin}${window.location.pathname}`,
      });
      onClose();
   }, [onClose]);

   const { isSuccess, data: cart } = useCart();
   // we only want to track the analytics when the cart drawer is opened by the user
   const onOpenWithAnalytics = useCallback(
      (event?: Event, autoOpened?: boolean) => {
         // Make sure the cart data is loaded and successful
         if (isSuccess && cart && !autoOpened && cart.lineItems.length) {
            trackGA4ViewCart({
               currency: cart.lineItems[0].price.currencyCode,
               value: cart.lineItems.reduce(
                  (acc: number, item) => acc + Number(item.price.amount) * item.quantity,
                  0
               ),
               items: convertCartLineItemsToAnalyticsItem(cart.lineItems),
               shopifyCartId: cart.shopifyCartId,
            });
         }
         onOpen();
      },
      [onOpen, isSuccess, cart]
   );

   return { ...context, onOpen: onOpenWithAnalytics, onViewCart };
}
